import {
  EoWriter,
  MapType,
  PacketAction,
  PacketFamily,
  SkillTargetType,
  SkillType,
  SpellRequestClientPacket,
  SpellTargetGroupClientPacket,
  SpellTargetOtherClientPacket,
  SpellTargetSelfClientPacket,
  SpellTargetType,
} from 'eolib';

import type { Client } from '@/client';
import {isCompanion,ownCompanion,bookSummonSlot,summonFromButton} from '@/summoning';
import { skillStatus } from '@/skill-status';
import { pvpState } from '@/pvp';
import { masteryRange,masteryManaCost } from '@/spell-mastery';
import { SPELL_COOLDOWN_TICKS } from '@/consts';
import { EOResourceID } from '@/edf';
import { SpellTarget } from '@/game-state';
import {
  CharacterDeathAnimation,
  CharacterSpellChantAnimation,
  EffectAnimation,
  type EffectTarget,
  EffectTargetCharacter,
  EffectTargetNpc,
  EffectTargetTile,
  NpcDeathAnimation,
} from '@/render';
import { SfxId } from '@/sfx';
import { SlotType } from '@/ui/enums';
import { getTimestamp } from './movement-controller';

enum SpellState {
  None = 0,
  Chanting = 1,
  Casting = 2,
}

export class SpellController {
  private client: Client;
  state = SpellState.None;
  selectedSpellId = 0;
  queuedSpellId = 0;
  spellCastTimestamp = 0;
  spellTarget: SpellTarget | null = null;
  spellTargetId = 0;
  spellCooldownTicks = 0;

  private spellQueuedSubscribers: (() => void)[] = [];

  constructor(client: Client) {
    this.client = client;
  }

  subscribeSpellQueued(cb: () => void): void {
    this.spellQueuedSubscribers.push(cb);
  }

  unsubscribeSpellQueued(cb: () => void): void {
    this.spellQueuedSubscribers = this.spellQueuedSubscribers.filter(
      (s) => s !== cb,
    );
  }

  private notifySpellQueued(): void {
    for (const cb of this.spellQueuedSubscribers) cb();
  }

  clearTarget(): void {
    this.spellTarget = null;
    this.spellTargetId = 0;
  }

  hasPendingSpellAction(): boolean {
    return this.queuedSpellId > 0 || this.state !== SpellState.None;
  }

  cancelTargeting(clearSelectedSpell = false): void {
    // Cancel only the chant we own; never destroy a walking/attack animation.
    const animations = this.client.animationController.characterAnimations;
    if (animations.get(this.client.playerId) instanceof CharacterSpellChantAnimation) {
      animations.delete(this.client.playerId);
    }
    this.queuedSpellId = 0;
    this.state = SpellState.None;
    this.clearTarget();
    if (clearSelectedSpell) {
      this.selectedSpellId = 0;
      this.notifySpellQueued();
    }
  }

  private cancelQueuedSpell(clearTarget = false): void {
    this.queuedSpellId = 0;
    this.spellCooldownTicks = 0;
    this.state = SpellState.None;
    if (clearTarget) {
      this.clearTarget();
    }
  }

  canSelectedSpellTargetNpc(npcIndex?:number): boolean {
    if (!this.selectedSpellId) {
      return false;
    }

    const record = this.client.getEsfRecordById(this.selectedSpellId);
    if(npcIndex!==undefined&&isCompanion(this.client,npcIndex))return ownCompanion(this.client)?.index===npcIndex&&!!record&&record.type===SkillType.Heal&&record.targetType===SkillTargetType.Normal;
    return !!record && record.type === SkillType.Attack;
  }

  queueSelectedSpellForNpc(npcIndex: number): boolean {
    if (
      !this.canSelectedSpellTargetNpc(npcIndex) ||
      this.queuedSpellId > 0 ||
      this.spellCooldownTicks > 0 ||
      this.state !== SpellState.None
    ) {
      return false;
    }

    this.spellTarget = SpellTarget.Npc;
    this.spellTargetId = npcIndex;
    this.queuedSpellId = this.selectedSpellId;
    this.notifySpellQueued();
    return true;
  }

  useHotbarSlot(index: number): void {
    if(bookSummonSlot(this.client,index)!==null){summonFromButton(this.client);return;}
    const slot = this.client.hotbarSlots[index];
    if (!slot) {
      return;
    }

    if (slot.type === SlotType.Item) {
      this.client.inventoryController.useItem(slot.typeId);
    } else {
      if (!this.client.spells.find((s) => s.id === slot.typeId)) {
        return;
      }

      const record = this.client.getEsfRecordById(slot.typeId);
      if (!record) {
        return;
      }

      if (skillStatus(this.client, slot.typeId).cooldown > 0) return;

      // Selecting any spell takes ownership from click-to-attack immediately.
      this.client.npcController.cancelAutoAttack();
      this.client.movementController.autoWalkPath = [];

      // Keep one requested cast while the current movement/attack finishes.
      // A held hotkey must not repeatedly restart the same chant.
      if (this.state === SpellState.Chanting) {
        if (this.queuedSpellId === slot.typeId) return;
        this.cancelTargeting();
      }

      // TODO: Bard
      if (record.type === SkillType.Bard) {
        this.client.bardController.openDialog();
        return;
      }

      if (
        record.targetType === SkillTargetType.Group &&
        !this.client.partyController.members.length
      ) {
        return;
      }

      if (
        [SkillTargetType.Self, SkillTargetType.Group].includes(
          record.targetType,
        )
      ) {
        this.client.npcController.cancelAutoAttack();
        this.client.npcController.clearCombatTarget();
        this.client.movementController.autoWalkPath = [];
        this.selectedSpellId = 0;
        this.spellTarget =
          record.targetType === SkillTargetType.Self
            ? SpellTarget.Self
            : SpellTarget.Group;
        this.spellTargetId = 0;
        this.queuedSpellId = slot.typeId;
        this.notifySpellQueued();
        return;
      }

      this.selectedSpellId = slot.typeId;
      this.queuedSpellId = 0;
      this.clearTarget();

      if (record.type === SkillType.Attack && !pvpState(this.client).forced()) {
        const targetIndex = this.client.npcController.selectedCombatNpcIndex;
        if (targetIndex !== null && this.client.getNpcByIndex(targetIndex)) {
          this.spellTarget = SpellTarget.Npc;
          this.spellTargetId = targetIndex;
          this.queuedSpellId = slot.typeId;
          this.notifySpellQueued();
          this.client.audioController.playById(SfxId.SpellActivate);
          return;
        }
      }

      this.notifySpellQueued();
      this.client.audioController.playById(SfxId.SpellActivate);
    }
  }

  private validNpcTarget(index: number): boolean {
    const npc = this.client.getNpcByIndex(index);
    if (!npc || this.client.animationController.npcAnimations.get(index) instanceof NpcDeathAnimation) return false;
    const origin = this.client.getPlayerCoords();
    const spell = this.client.getEsfRecordById(this.queuedSpellId);
    if(spell?.type===SkillType.Heal&&ownCompanion(this.client)?.index!==index)return false;
    const range = masteryRange(this.client,this.queuedSpellId);
    return Math.abs(origin.x - npc.coords.x) + Math.abs(origin.y - npc.coords.y) <= range;
  }

  beginSpellChant(): void {
    const record = this.client.getEsfRecordById(this.queuedSpellId);
    if (!record) {
      this.cancelQueuedSpell(true);
      return;
    }

    // Let an already-started step finish; never replace its animation.
    if (this.client.animationController.characterAnimations.has(this.client.playerId)) {
      return;
    }
    this.client.npcController.cancelAutoAttack();
    this.client.movementController.autoWalkPath = [];

    if (this.client.tp < masteryManaCost(this.client,this.queuedSpellId)) {
      this.client.toastController.showWarning(
        this.client.getResourceString(EOResourceID.ATTACK_YOU_ARE_EXHAUSTED_TP),
      );
      this.cancelQueuedSpell(false);
      return;
    }

    if (
      record.type === SkillType.Heal &&
      this.spellTarget === SpellTarget.Npc &&
      ownCompanion(this.client)?.index !== this.spellTargetId
    ) {
      this.cancelQueuedSpell(true);
      return;
    }

    if (
      record.type === SkillType.Attack &&
      this.spellTarget !== SpellTarget.Npc &&
      this.client.map!.type !== MapType.Pk &&
      !(this.spellTarget === SpellTarget.Player && pvpState(this.client).forced())
    ) {
      this.cancelQueuedSpell(true);
      return;
    }

    if (this.spellTarget === SpellTarget.Npc) {
      const npc = this.client.getNpcByIndex(this.spellTargetId);
      if (!npc || !this.validNpcTarget(this.spellTargetId)) {
        const range = masteryRange(this.client,this.queuedSpellId);
        this.client.toastController.showWarning(`Target is out of range. ${record.name} reaches ${range} tile${range === 1 ? '' : 's'}.`);
        this.cancelQueuedSpell(true);
        return;
      }

      const animation = this.client.animationController.npcAnimations.get(
        npc.index,
      );
      if (animation instanceof NpcDeathAnimation) {
        this.cancelQueuedSpell(true);
        return;
      }
    }

    if (this.spellTarget === SpellTarget.Player) {
      const character = this.client.getCharacterById(this.spellTargetId);
      if (!character) {
        this.cancelQueuedSpell(true);
        return;
      }
      const origin=this.client.getPlayerCoords();
      if(Math.abs(origin.x-character.coords.x)+Math.abs(origin.y-character.coords.y)>masteryRange(this.client,this.queuedSpellId)){
        this.client.toastController.showWarning('Target is beyond this spell’s casting reach.');this.cancelQueuedSpell(true);return;
      }

      const animation = this.client.animationController.characterAnimations.get(
        this.spellTargetId,
      );
      if (animation instanceof CharacterDeathAnimation) {
        this.cancelQueuedSpell(true);
        return;
      }
    }

    this.spellCastTimestamp = getTimestamp();
    this.state = SpellState.Chanting;
    const packet = new SpellRequestClientPacket();
    packet.spellId = this.queuedSpellId;
    packet.timestamp = this.spellCastTimestamp;
    this.client.bus!.send(packet);

    this.client.animationController.characterAnimations.set(
      this.client.playerId,
      new CharacterSpellChantAnimation(
        this.client.sans11,
        this.queuedSpellId,
        record.chant,
        record.castTime,
      ),
    );
  }

  castSpell(spellId: number): void {
    if (this.state !== SpellState.Chanting || this.queuedSpellId !== spellId ||
        this.spellTarget === null) {
      this.cancelQueuedSpell(true);
      return;
    }
    if (this.spellTarget === SpellTarget.Npc && !this.validNpcTarget(this.spellTargetId)) {
      this.client.toastController.showWarning('Target moved out of range. Move closer and cast again.');
      this.cancelQueuedSpell(true);
      return;
    }
    const timestamp = getTimestamp();
    const character = this.client.getPlayerCharacter();
    const record = this.client.getEsfRecordById(spellId);

    switch (this.spellTarget) {
      case SpellTarget.Self: {
        const packet = new SpellTargetSelfClientPacket();
        packet.spellId = spellId;
        packet.direction = character!.direction!;
        packet.timestamp = timestamp;
        this.client.bus!.send(packet);
        break;
      }
      case SpellTarget.Group: {
        const packet = new SpellTargetGroupClientPacket();
        packet.spellId = spellId;
        packet.timestamp = timestamp;
        this.client.bus!.send(packet);
        break;
      }
      default: {
        const packet = new SpellTargetOtherClientPacket();
        packet.spellId = spellId;
        packet.targetType =
          this.spellTarget === SpellTarget.Npc
            ? SpellTargetType.Npc
            : SpellTargetType.Player;
        packet.victimId = this.spellTargetId;
        packet.previousTimestamp = this.spellCastTimestamp;
        packet.timestamp = timestamp;
        if (
          this.spellTarget === SpellTarget.Player &&
          record?.type === SkillType.Attack &&
          this.client.map!.type !== MapType.Pk &&
          pvpState(this.client).forced()
        ) {
          const writer = new EoWriter();
          packet.serialize(writer);
          this.client.bus!.sendBuf(PacketFamily.Spell, PacketAction.Accept, writer.toByteArray());
        } else {
          this.client.bus!.send(packet);
        }
        break;
      }
    }

    this.queuedSpellId = 0;
    this.spellCooldownTicks = SPELL_COOLDOWN_TICKS;
    this.state = SpellState.Casting;
  }

  playSpellEffect(spellId: number, target: EffectTarget): void {
    const record = this.client.getEsfRecordById(spellId);
    if (!record) {
      return;
    }

    const metadata = this.client.getEffectMetadata(record.graphicId);
    if (!metadata) {
      return;
    }

    this.client.animationController.effects.push(
      new EffectAnimation(record.graphicId, target, metadata),
    );

    if (!metadata.sfx) {
      return;
    }

    if (target instanceof EffectTargetCharacter) {
      if (target.playerId === this.client.playerId) {
        this.client.audioController.playById(metadata.sfx);
        return;
      }

      const position = this.client.getCharacterById(target.playerId)?.coords;
      if (position) {
        this.client.audioController.playAtPosition(metadata.sfx, position);
        return;
      }
    }

    if (target instanceof EffectTargetNpc) {
      const position = this.client.getNpcByIndex(target.index)?.coords;
      if (position) {
        this.client.audioController.playAtPosition(metadata.sfx, position);
        return;
      }
    }

    if (target instanceof EffectTargetTile) {
      this.client.audioController.playAtPosition(metadata.sfx, target.coords);
      return;
    }

    this.client.audioController.playById(metadata.sfx);
  }

  tick(): void {
    if (this.client.worldLoading) return;
    this.spellCooldownTicks = Math.max(this.spellCooldownTicks - 1, 0);

    // A warp, death or server refresh can remove the local chant animation.
    // Release the casting state as well, so the next cast works without reload.
    if (this.state === SpellState.Chanting) {
      const animation = this.client.animationController.characterAnimations.get(this.client.playerId);
      if (!(animation instanceof CharacterSpellChantAnimation)) {
        this.cancelQueuedSpell(true);
      }
    }

    if (this.queuedSpellId && this.state === SpellState.None && !this.spellCooldownTicks) {
      this.beginSpellChant();
    } else if (!this.spellCooldownTicks && this.state === SpellState.Casting) {
      this.state = SpellState.None;
    }
  }
}
